import { Tone } from '@gokrew/models/ui.types';
import type {
  Announcement,
  AnnouncementAudience,
  AnnouncementDetail,
} from '@gokrew/models/announcements';
import {
  FileText,
  FileSpreadsheet,
  Image,
  Film,
  Music,
  Archive,
  Paperclip,
  type LucideIcon,
} from 'lucide-react';
import { formatDate } from './utility';
import { formatNotificationTime } from './notifications';

/** "Oct 11, 2026 2:30 PM" / "11 oct. 2026 14:30", in the given language. */
export function formatAnnouncementDate(
  dateStr: string | Date,
  locale?: string
): string {
  return formatDate(dateStr, { format: 'mediumTime', locale });
}

export function getAnnouncementStatus(announcement: Announcement): {
  label: string;
  tone: Tone;
} {
  const now = new Date();
  const publishAt = new Date(announcement.publishAt);
  const expireAt = announcement.expiresAt
    ? new Date(announcement.expiresAt)
    : null;

  if (publishAt > now) {
    return { label: 'Scheduled', tone: Tone.WARNING };
  }

  if (expireAt && expireAt <= now) {
    return { label: 'Expired', tone: Tone.ERROR };
  }

  return { label: 'Active', tone: Tone.SUCCESS };
}

export function getAudienceSummary(
  audience: AnnouncementAudience | undefined | null
): string {
  if (!audience) return 'All employees';

  if (audience.type === 'all') return 'All employees';

  if (audience.type === 'dept_designation') {
    const parts: string[] = [];
    if (audience.departmentIds?.length) {
      parts.push(
        `${audience.departmentIds.length} dept${audience.departmentIds.length > 1 ? 's' : ''}`
      );
    }
    if (audience.designationIds?.length) {
      parts.push(
        `${audience.designationIds.length} designation${audience.designationIds.length > 1 ? 's' : ''}`
      );
    }
    return parts.join(', ') || 'Dept. & Job Title';
  }

  if (audience.type === 'manual_pick') {
    const count = audience.employeeIds?.length ?? 0;
    return `${count} employee${count !== 1 ? 's' : ''}`;
  }

  return 'All employees';
}

export function getDefaultAnnouncementFormValues() {
  return {
    title: '',
    description: '',
    isPinned: false,
    scheduleType: 'immediate' as const,
    publishAt: '',
    expireAt: '',
    files: [] as File[],
  };
}

/** "2 hours ago" / "il y a 2 heures", in the given language. */
export function relativeTime(
  isoString: string | Date,
  locale: string = 'en'
): string {
  return formatNotificationTime(new Date(isoString).toISOString(), locale);
}

export function getStatusTone(announcement: Announcement): Tone {
  const { label } = getAnnouncementStatus(announcement);
  switch (label.toLowerCase()) {
    case 'published':
      return Tone.SUCCESS;
    case 'expired':
      return Tone.ERROR;
    case 'scheduled':
      return Tone.WARNING;
    default:
      return Tone.SUCCESS;
  }
}

/**
 * Maps the raw backend announcement shape to the frontend Announcement model.
 * The backend returns shareScope / hideScope directly on the row; the frontend
 * model wraps these into an `audience` object.
 */
export function normalizeAnnouncement(raw: AnnouncementDetail): Announcement {
  const shareScope: string = raw.shareScope ?? 'none';

  let audienceType: AnnouncementAudience['type'] = 'all';

  if (shareScope === 'org') {
    audienceType = 'dept_designation';
  } else if (shareScope === 'employee') {
    audienceType = 'manual_pick';
  }

  const shareTargets = raw.shareTargets ?? [];
  const hideTargets = raw.hideTargets ?? [];

  const departmentIds = shareTargets
    .filter((t) => t.targetType === 'department')
    .map((t) => t.targetId);

  const designationIds = shareTargets
    .filter((t) => t.targetType === 'designation')
    .map((t) => t.targetId);

  const employeeIds = shareTargets
    .filter((t) => t.targetType === 'employee')
    .map((t) => t.targetId);

  const hideDepartmentIds = hideTargets
    .filter((t) => t.targetType === 'department')
    .map((t) => t.targetId);

  const hideDesignationIds = hideTargets
    .filter((t) => t.targetType === 'designation')
    .map((t) => t.targetId);

  const hideEmployeeIds = hideTargets
    .filter((t) => t.targetType === 'employee')
    .map((t) => t.targetId);

  // Normalize attachments from backend DTO
  const attachments =
    raw.attachments?.map((attachment) => ({
      id: attachment.id,
      key:
        attachment.key ??
        attachment.fileUrl.split('.amazonaws.com/')[1]?.split('?')[0] ??
        '',
      url: attachment.fileUrl,
      name: attachment.fileName,
      type: attachment.fileType,
      size: Number(attachment.fileSize ?? 0),
    })) ?? [];

  return {
    ...raw,

    attachments,
    expiresAt: raw.expiresAt ?? undefined,
    author: raw.createdByUser
      ? `${raw.createdByUser.firstName ?? ''} ${raw.createdByUser.lastName ?? ''}`.trim()
      : 'Unknown',

    audience: {
      type: audienceType,
      departmentIds,
      designationIds,
      employeeIds,
    },
    hideScope: raw.hideScope,

    hideDepartmentIds,
    hideDesignationIds,
    hideEmployeeIds,
  };
}

type AttachmentStyle = {
  Icon: LucideIcon;
  bg: string;
  color: string;
};

export function getAttachmentStyle(filename: string): AttachmentStyle {
  const ext = filename.split('.').pop()?.toLowerCase();
  const map: Record<string, AttachmentStyle> = {
    pdf: { Icon: FileText, bg: '#FCEBEB', color: '#A32D2D' },
    doc: { Icon: FileText, bg: '#E6F1FB', color: '#185FA5' },
    docx: { Icon: FileText, bg: '#E6F1FB', color: '#185FA5' },
    xls: { Icon: FileSpreadsheet, bg: '#EAF3DE', color: '#3B6D11' },
    xlsx: { Icon: FileSpreadsheet, bg: '#EAF3DE', color: '#3B6D11' },
    jpg: { Icon: Image, bg: '#EAF3DE', color: '#3B6D11' },
    jpeg: { Icon: Image, bg: '#EAF3DE', color: '#3B6D11' },
    png: { Icon: Image, bg: '#EAF3DE', color: '#3B6D11' },
    gif: { Icon: Image, bg: '#EAF3DE', color: '#3B6D11' },
    zip: { Icon: Archive, bg: '#FAEEDA', color: '#854F0B' },
    mp4: { Icon: Film, bg: '#FBEAF0', color: '#993556' },
    mp3: { Icon: Music, bg: '#FBEAF0', color: '#993556' },
  };
  return map[ext ?? ''] ?? { Icon: Paperclip, bg: '#F1EFE8', color: '#5F5E5A' };
}
